import {CHANNELS} from "data/products";
import {MODUL_FONT_SET} from "data/products/index";
import {
  drawnClientTemplates,
  drawnTemplatesFor,
  RELEASES_OPT_IN_TEMPLATE_IDS,
} from "data/templates";
import {KNOWN_TEMPLATE_IDS_BY_CHANNEL} from "data/templateManifest";
import {drawnReleasesSetTemplateIds, drawnTemplateId} from "data/templateSlots";
import {isReleaseTemplateAvailable} from "data/template-availability";
import {airLayoutTemplateIds} from "libs/airDesign";

export function getReleaseProductCodesForType(typeValue) {
  const releasesChannel = CHANNELS.find((channel) => channel.value === "releases");
  if (!releasesChannel?.types?.length || !typeValue) return [];

  const type = releasesChannel.types.find((item) => item.value === typeValue);
  if (!type?.products || typeof type.products !== "object") return [];

  return Object.keys(type.products);
}

const RELEASES_TEMPLATE_IDS = new Set(KNOWN_TEMPLATE_IDS_BY_CHANNEL.releases);

/**
 * The template ids a releases page offers: the set's own, then the banners an
 * admin added to the page from other sets (the `extraTemplates` scene field).
 * An added id names its place on the page, so it gives the design on air there
 * (data/templateSlots.ts). An id that no releases template has is left out: a
 * saved list can outlive a template.
 * @param {string | null | undefined} selectedSet
 * @param {readonly string[] | null | undefined} extraTemplateIds
 * @param {import("data/templateSlots").TemplateSelection} selection
 * @returns {string[]}
 */
export function getReleaseTemplateIdsForSet(selectedSet, extraTemplateIds, selection) {
  if (!selection) throw new Error("Releases banners need a template selection");
  const templateIds = drawnReleasesSetTemplateIds(selection)[selectedSet];
  if (!Array.isArray(templateIds)) return [];
  const extras = (extraTemplateIds ?? [])
    .filter((id) => RELEASES_TEMPLATE_IDS.has(id))
    .map((id) => drawnTemplateId("releases", id, selection))
    .filter((id, index, ids) => !templateIds.includes(id) && ids.indexOf(id) === index);
  return extras.length > 0 ? [...templateIds, ...extras] : templateIds;
}

/**
 * The ALL-products view draws each template once per product: `<templateId>-<code>`.
 * @param {readonly string[]} productCodes
 * @param {string | null | undefined} selectedSet
 * @param {readonly string[] | null | undefined} extraTemplateIds
 * @param {import("data/templateSlots").TemplateSelection} selection
 * @returns {string[]}
 */
export function getAllProductsTemplateInstanceIds(
  productCodes,
  selectedSet,
  extraTemplateIds,
  selection
) {
  const templateIds = getReleaseTemplateIdsForSet(selectedSet, extraTemplateIds, selection);
  if (templateIds.length === 0) return [];

  const codes = Array.isArray(productCodes) ? productCodes : [];
  return codes.flatMap((code) => templateIds.map((templateId) => `${templateId}-${code}`));
}

/**
 * The banners of a set that at least one of `productValues` draws on this
 * layout, in the channel's order. One product for a product's page; every
 * product of the ALL-products view, where a .NET product draws the .NET
 * splash and the others the plain one.
 * @param {{
 *   selectedSet: string | null | undefined,
 *   layoutOption: number | string,
 *   productValues: readonly string[],
 *   selection: import("data/templateSlots").TemplateSelection,
 * }} options
 */
export function getSetTemplatesForProducts({selectedSet, layoutOption, productValues, selection}) {
  return drawnForProducts(
    getReleaseTemplateIdsForSet(selectedSet, [], selection),
    layoutOption,
    productValues,
    selection
  );
}

/** The group of the banners of no set (`optIn` in data/templateManifest.ts). */
export const OPT_IN_GROUP = {label: "Banners of no set", value: "opt-in"};

/**
 * The banners of the other sets that a page can add, by set, in the channel's
 * set order, and after them the banners of no set (OPT_IN_GROUP). No page
 * draws a banner of no set until an admin adds it, so a new banner can go to
 * one stage only. Only the banners that draw for the page's products on this
 * layout are offered (getSetTemplatesForProducts).
 * @param {{
 *   selectedSet: string | null | undefined,
 *   layoutOption: number | string,
 *   productValues: readonly string[],
 *   sets: ReadonlyArray<{label: string, value: string}>,
 *   optInTemplateIds?: readonly string[],
 *   selection: import("data/templateSlots").TemplateSelection,
 * }} options
 */
export function getOtherSetTemplates({
  selectedSet,
  layoutOption,
  productValues,
  sets,
  optInTemplateIds = RELEASES_OPT_IN_TEMPLATE_IDS,
  selection,
}) {
  const optInIds = optInTemplateIds
    .map((id) => drawnTemplateId("releases", id, selection))
    .filter((id, index, ids) => ids.indexOf(id) === index);
  const optIn = {
    set: OPT_IN_GROUP,
    templates: drawnForProducts(optInIds, layoutOption, productValues, selection),
  };
  return [
    ...sets
      .filter((set) => set.value !== selectedSet)
      .map((set) => ({
        set,
        templates: getSetTemplatesForProducts({
          selectedSet: set.value,
          layoutOption,
          productValues,
          selection,
        }),
      })),
    optIn,
  ].filter((group) => group.templates.length > 0);
}

/**
 * The templates of `templateIds` that at least one of `productValues` draws
 * on this layout, in the channel's order.
 * @param {readonly string[]} templateIds
 * @param {number | string} layoutOption
 * @param {readonly string[]} productValues
 * @param {import("data/templateSlots").TemplateSelection} selection
 */
function drawnForProducts(templateIds, layoutOption, productValues, selection) {
  if (templateIds.length === 0) return [];
  // One template per id: where two products draw different templates under
  // one id (the emails of JetBrains Air), the first product's is the one.
  const byId = new Map();
  for (const productValue of productValues) {
    for (const template of drawnReleaseTemplates({
      templateIds,
      layoutOption,
      productValue,
      selection,
    })) {
      if (!byId.has(template.id)) byId.set(template.id, template);
    }
  }
  return drawnClientTemplates("releases", selection).filter(
    (template) => byId.get(template.id) === template
  );
}

/**
 * The banners a releases page draws: the set's own and the ones added from
 * other sets, for this product and layout. A .NET product draws the .NET
 * splashes (`drawnFor` in templateManifest.ts).
 * @param {{
 *   selectedSet: string | null | undefined,
 *   layoutOption: number | string,
 *   productValue: string | null | undefined,
 *   extraTemplateIds: readonly string[] | null | undefined,
 *   selection: import("data/templateSlots").TemplateSelection,
 * }} options
 */
export function getFilteredReleaseTemplates({
  selectedSet,
  layoutOption,
  productValue,
  extraTemplateIds,
  selection,
}) {
  const allowedIds = getReleaseTemplateIdsForSet(selectedSet, extraTemplateIds, selection);
  return drawnReleaseTemplates({templateIds: allowedIds, layoutOption, productValue, selection});
}

/**
 * The templates of `templateIds` that `productValue` draws on this layout, in
 * the channel's order.
 * @param {{
 *   templateIds: readonly string[],
 *   layoutOption: number | string,
 *   productValue: string | null | undefined,
 *   selection: import("data/templateSlots").TemplateSelection,
 * }} options
 */
function drawnReleaseTemplates({templateIds: allowedIds, layoutOption, productValue, selection}) {
  if (allowedIds.length === 0) return [];

  const currentLayout = Number(layoutOption);
  // A product with its own design draws a layout on the canvases its frames
  // cover and no others: Air's screenshot layout exists for three banners
  // (AIR_LAYOUT_TEMPLATES in libs/airDesign.ts). `MODUL_FONT_SET` is the same
  // membership `isAirBanner` reads, so the list and the skin cannot drift.
  const airTemplateIds = MODUL_FONT_SET.has(productValue)
    ? airLayoutTemplateIds(currentLayout)
    : null;

  return drawnTemplatesFor("releases", productValue, selection).filter((template) => {
    if (!allowedIds.includes(template.id)) return false;
    if (airTemplateIds && !airTemplateIds.includes(template.id)) return false;
    return isReleaseTemplateAvailable(template, productValue, currentLayout);
  });
}
